import { useState, useEffect, useCallback } from 'react';
import { Wrench } from 'lucide-react';
import { usePlatformConfig } from '@/hooks/usePlatformConfig';
import { PLATFORM_CONFIG } from '@/lib/constants';

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

function getTimeLeft(targetMs: number): TimeLeft {
  const diff = Math.max(0, targetMs - Date.now());
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
}

const TimeUnit = ({ value, label }: { value: number; label: string }) => (
  <div className="flex flex-col items-center">
    <span className="text-4xl font-bold text-foreground tabular-nums">
      {String(value).padStart(2, '0')}
    </span>
    <span className="text-xs text-muted-foreground uppercase tracking-wider mt-1">{label}</span>
  </div>
);

const Separator = () => (
  <span className="text-3xl font-bold text-muted-foreground/50 self-start mt-1">:</span>
);

export default function MaintenancePage() {
  const { data: endTimeStr } = usePlatformConfig('maintenance_end_time');

  // Parse the end time — if invalid or empty, show no countdown
  const targetMs = endTimeStr ? new Date(endTimeStr).getTime() : NaN;
  const hasCountdown = !isNaN(targetMs) && targetMs > Date.now();

  const [timeLeft, setTimeLeft] = useState<TimeLeft>(
    hasCountdown ? getTimeLeft(targetMs) : { days: 0, hours: 0, minutes: 0, seconds: 0 }
  );

  const tick = useCallback(() => {
    if (hasCountdown) {
      setTimeLeft(getTimeLeft(targetMs));
    }
  }, [hasCountdown, targetMs]);

  useEffect(() => {
    if (!hasCountdown) return;
    const id = setInterval(tick, PLATFORM_CONFIG.COUNTDOWN_INTERVAL_MS);
    return () => clearInterval(id);
  }, [tick, hasCountdown]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md rounded-xl border border-border bg-card p-8 shadow-sm flex flex-col items-center text-center space-y-6">
        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
          <Wrench className="w-8 h-8 text-muted-foreground" />
        </div>

        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-foreground font-display">
            Under Maintenance
          </h1>
          <p className="text-sm text-muted-foreground">
            {hasCountdown
              ? "We're making improvements. We'll be back shortly."
              : "We're making improvements. We'll be back soon."}
          </p>
        </div>

        {hasCountdown && (
          <div className="flex items-center gap-3">
            <TimeUnit value={timeLeft.days} label="Days" />
            <Separator />
            <TimeUnit value={timeLeft.hours} label="Hours" />
            <Separator />
            <TimeUnit value={timeLeft.minutes} label="Minutes" />
            <Separator />
            <TimeUnit value={timeLeft.seconds} label="Seconds" />
          </div>
        )}

        <p className="text-xs text-muted-foreground">
          Thank you for your patience.
        </p>
      </div>
    </div>
  );
}
